import { useCallback, useEffect, useMemo, useState } from "react";
import { ChirpConfigs, ClientEndpoints, EventTriggers, TrackerConfigs } from "@app/shared/src/api/api.gen";
import {
    ChirpConfig,
    ClientEndpoint,
    Device,
    EventTrigger,
    TrackerConfig,
} from "@app/shared/src/models";
import { AlertCategory, categoriseTrigger } from "../helpers/alertCategories";
import { setSpeedingLimit } from "../helpers/alertSettings";
import { useFleet } from "../components/providers/FleetProvider";
import { useOrg } from "../components/providers/OrgProvider";

// Re-exported so existing importers of this hook keep working now that the categorisation lives in
// a React-free helper the pure settings modules can share.
export type { AlertCategory };
export { categoriseTrigger };

/**
 * Fleet defaults: reading what every unit is set to, and changing it everywhere at once.
 *
 * There is no org-level settings row on the server — configuration lives on each device and each
 * trigger. So a "fleet default" here means the value the majority of units share, and changing it
 * writes that value to every unit that was following the old one. Units someone has deliberately
 * moved off the shared value keep their setting; that is what makes this safe to press.
 */


export type TriggerGroup = {
    category: AlertCategory;
    triggers: { trigger: EventTrigger; device: Device }[];
};



export type FleetDefaultsState = {
    devices: Device[];
    /** Every trigger in the fleet, paired with the device it governs. */
    triggerGroups: Map<AlertCategory, { trigger: EventTrigger; device: Device }[]>;
    /** The raw per-site triggers, for callers that compare a whole unit rather than one category. */
    triggersByFacility?: Map<string, EventTrigger[]>;
    chirpConfigs: ChirpConfig[];
    trackerConfigs: TrackerConfig[];
    emailEndpoints: ClientEndpoint[];
    /**
     * The fleet's relay boards, by site.
     *
     * Kept alongside the email ones because a trigger that has never driven a relay needs the
     * endpoint itself to build the join — see applyRelaySelection. Not filtered on a populated
     * field the way email is: an email endpoint with no addresses reaches nobody, whereas a relay
     * board's usefulness is its existence.
     */
    relayEndpoints: ClientEndpoint[];
    isLoading: boolean;
    error?: string;
    reload: () => void;
};

export function useFleetDefaults(): FleetDefaultsState {
    const { org } = useOrg();
    // The fleet, not the whole organization: the Settings page derives its push targets from this,
    // and the unfiltered roster had "Apply to N devices" reconfiguring traffic counters.
    const { statsDevices: devices, loadTriggers, invalidateTriggers } = useFleet();
    const [triggersByFacility, setTriggersByFacility] = useState<Map<string, EventTrigger[]>>();
    const [chirpConfigs, setChirpConfigs] = useState<ChirpConfig[]>([]);
    const [trackerConfigs, setTrackerConfigs] = useState<TrackerConfig[]>([]);
    const [emailEndpoints, setEmailEndpoints] = useState<ClientEndpoint[]>([]);
    const [relayEndpoints, setRelayEndpoints] = useState<ClientEndpoint[]>([]);
    const [error, setError] = useState<string>();
    const [nonce, setNonce] = useState(0);

    useEffect(() => {
        if (devices.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const [triggers, chirps, trackers, endpoints] = await Promise.all([
                    loadTriggers(),
                    ChirpConfigs.getAll().catch(() => [] as ChirpConfig[]),
                    TrackerConfigs.getAll().catch(() => [] as TrackerConfig[]),
                    org?.id ? ClientEndpoints.getAll(org.id).catch(() => [] as ClientEndpoint[]) : Promise.resolve([] as ClientEndpoint[]),
                ]);
                if (cancelled) return;
                setTriggersByFacility(triggers);
                setChirpConfigs(chirps ?? []);
                setTrackerConfigs(trackers ?? []);
                setEmailEndpoints((endpoints ?? []).filter((e) => (e as { emailAddress?: string }).emailAddress));
                setRelayEndpoints((endpoints ?? []).filter((e) => (e as { $type?: string }).$type?.includes("WebRelay")));
            } catch (err) {
                if (!cancelled) setError(err instanceof Error ? err.message : "Could not load fleet settings");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [devices, loadTriggers, org?.id, nonce]);

    const triggerGroups = useMemo(() => {
        const groups = new Map<AlertCategory, { trigger: EventTrigger; device: Device }[]>();
        if (!triggersByFacility) return groups;
        for (const device of devices) {
            const triggers = (device.facilityId && triggersByFacility.get(device.facilityId)) || [];
            for (const trigger of triggers) {
                const category = categoriseTrigger(trigger);
                if (!category) continue;
                const list = groups.get(category) ?? [];
                // One device per trigger row: a trigger shared by a facility's devices would
                // otherwise be counted once per truck.
                if (!list.some((x) => x.trigger.id === trigger.id)) list.push({ trigger, device });
                groups.set(category, list);
            }
        }
        return groups;
    }, [devices, triggersByFacility]);

    return {
        devices,
        triggerGroups,
        triggersByFacility,
        chirpConfigs,
        trackerConfigs,
        emailEndpoints,
        relayEndpoints,
        isLoading: triggersByFacility === undefined,
        error,
        // Reload is called after a push wrote triggers, so the shared cache must be dropped first —
        // re-running the effect against the cached promise would hand back the pre-push values.
        reload: useCallback(() => {
            invalidateTriggers();
            setNonce((n) => n + 1);
        }, [invalidateTriggers]),
    };
}






/**
 * Switch every given trigger on or off.
 *
 * A trigger with no explicit IsEnabled predates the field and is running, so "already matches" is
 * judged the same way isTriggerEnabled reads it — never `=== true`.
 */
export async function applyTriggerEnabled(entries: { trigger: EventTrigger }[], enabled: boolean): Promise<number> {
    let saved = 0;
    for (const { trigger } of entries) {
        if ((trigger.isEnabled !== false) === enabled) continue;
        trigger.isEnabled = enabled;
        await EventTriggers.update(trigger);
        saved += 1;
    }
    return saved;
}

/**
 * Move the speeding limit on every trigger in the category.
 *
 * Not applyTriggerValue("maxValue"): a trigger an older console wrote the limit to MinValue on
 * needs that floor cleared as well, or it keeps firing on slow traffic whatever MaxValue says.
 */
export async function applySpeedingLimit(entries: { trigger: EventTrigger }[], limitMps: number): Promise<number> {
    let saved = 0;
    for (const { trigger } of entries) {
        if (trigger.maxValue === limitMps && trigger.minValue == null) continue;
        setSpeedingLimit(trigger, limitMps);
        await EventTriggers.update(trigger);
        saved += 1;
    }
    return saved;
}

/** Write one numeric field on every trigger in a category. */
export async function applyTriggerValue(
    entries: { trigger: EventTrigger }[],
    field: "minValue" | "maxValue" | "additionalValue",
    value: number,
): Promise<number> {
    let saved = 0;
    for (const { trigger } of entries) {
        if (trigger[field] === value) continue;
        trigger[field] = value;
        await EventTriggers.update(trigger);
        saved += 1;
    }
    return saved;
}



/**
 * Relay pulse durations live in the trigger→endpoint join's `endpointConfig`, a JSON blob the
 * device parses (Shared/Models/Api/v1/ClientEndpoints/WebRelayEndpointConfig). Relay numbers are
 * the physical terminals: 1 is the beacons, 2 is the horn.
 */
type WebRelayConfig = { relay?: number; Relay?: number; pulseTime?: number; PulseTime?: number };
type WebRelayEndpointConfig = { relays?: WebRelayConfig[]; Relays?: WebRelayConfig[] };

function parseRelayConfig(raw: string | undefined): WebRelayEndpointConfig | undefined {
    if (!raw) return undefined;
    try {
        return JSON.parse(raw) as WebRelayEndpointConfig;
    } catch {
        // A device-authored blob we can't read is not something to guess at.
        return undefined;
    }
}

/** Pulse seconds currently configured for a relay terminal, across the fleet's triggers. */
export function relayPulseSeconds(entries: { trigger: EventTrigger }[], relayNumber: number): number | undefined {
    for (const { trigger } of entries) {
        for (const join of trigger.eventTriggerClientEndpoints ?? []) {
            const cfg = parseRelayConfig(join.endpointConfig);
            const relays = cfg?.relays ?? cfg?.Relays ?? [];
            const match = relays.find((r) => (r.relay ?? r.Relay) === relayNumber);
            const pulse = match?.pulseTime ?? match?.PulseTime;
            if (pulse != null) return pulse;
        }
    }
    return undefined;
}

/** Write a relay's pulse duration on every trigger that drives that terminal. */
export async function applyRelayPulse(
    entries: { trigger: EventTrigger }[],
    relayNumber: number,
    seconds: number,
): Promise<number> {
    let saved = 0;
    for (const { trigger } of entries) {
        let touched = false;
        for (const join of trigger.eventTriggerClientEndpoints ?? []) {
            const cfg = parseRelayConfig(join.endpointConfig);
            if (!cfg) continue;
            const relays = cfg.relays ?? cfg.Relays;
            if (!relays) continue;
            const match = relays.find((r) => (r.relay ?? r.Relay) === relayNumber);
            if (!match) continue;
            // Preserve whichever casing the stored blob uses — the device parses it, not us.
            if (match.pulseTime !== undefined || match.PulseTime === undefined) match.pulseTime = seconds;
            if (match.PulseTime !== undefined) match.PulseTime = seconds;
            join.endpointConfig = JSON.stringify(cfg);
            touched = true;
        }
        if (touched) {
            await EventTriggers.update(trigger);
            saved += 1;
        }
    }
    return saved;
}
